/**
 * useRealtimeEvents — singleton WebSocket for live model-change events.
 *
 * All components share ONE connection via RealtimeEventsProvider.
 * Each call to useRealtimeEvents() registers a subscriber callback;
 * the socket is opened once when the first subscriber mounts and stays
 * open until the provider unmounts.
 *
 * Usage (unchanged from before):
 *   const { connected } = useRealtimeEvents((event) => {
 *     if (event.model === 'projects.project') refetchProjects();
 *   });
 *
 * Wrap your app in <RealtimeEventsProvider> (done in App.jsx).
 *
 * Event payload shape:
 *   { type: "model.changed", model: "app.model", action: "create"|"update"|"delete"|"bulk_update", pk, data }
 */

import React, { createContext, useContext, useEffect, useRef, useState, useCallback, type ReactNode } from 'react';

export interface RealtimeEvent {
  type: string;
  model?: string;
  action?: string;
  pk?: number | string;
  data?: unknown;
}

type RealtimeSubscriber = (event: RealtimeEvent) => void;

interface RealtimeContextValue {
  connected: boolean;
  subscribe: (subscriber: RealtimeSubscriber) => () => void;
}

const INITIAL_DELAY = 1_000;
const MAX_DELAY     = 30_000;

function getWsUrl() {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws/live/`;
}

// ── Singleton context ────────────────────────────────────────────────────────

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function RealtimeEventsProvider({ children }: { children: ReactNode }) {
  const [connected, setConnected] = useState(false);
  const subscribersRef = useRef(new Set<RealtimeSubscriber>());
  const wsRef          = useRef(null);
  const delayRef       = useRef(INITIAL_DELAY);
  const timerRef       = useRef(null);
  const unmountedRef   = useRef(false);
  const manualCloseRef = useRef(false);

  const open = useCallback(() => {
    if (unmountedRef.current) return;
    clearTimeout(timerRef.current);
    // The browser sends the HttpOnly session cookie automatically with the
    // WebSocket upgrade request — no ?token= query parameter needed.
    const ws = new WebSocket(getWsUrl());
    wsRef.current = ws;

    ws.onopen = () => {
      if (unmountedRef.current) { ws.close(); return; }
      setConnected(true);
      delayRef.current = INITIAL_DELAY;
    };

    ws.onmessage = (evt) => {
      try {
        const payload = JSON.parse(evt.data);
        subscribersRef.current.forEach(fn => fn(payload));
      } catch (_) { /* ignore malformed frames */ }
    };

    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;
      if (unmountedRef.current) return;
      if (manualCloseRef.current) {
        manualCloseRef.current = false;
        return;
      }
      timerRef.current = setTimeout(() => {
        delayRef.current = Math.min(delayRef.current * 2, MAX_DELAY);
        open();
      }, delayRef.current);
    };

    ws.onerror = () => { ws.close(); };
  }, []);

  const reconnect = useCallback(() => {
    if (unmountedRef.current) return;
    clearTimeout(timerRef.current);
    delayRef.current = INITIAL_DELAY;

    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      manualCloseRef.current = true;
      ws.close();
    } else {
      open();
    }
  }, [open]);

  useEffect(() => {
    unmountedRef.current = false;
    open();
    return () => {
      unmountedRef.current = true;
      clearTimeout(timerRef.current);
      wsRef.current?.close();
    };
  }, [open]);

  useEffect(() => {
    // Reconnect when the session changes (login / logout) by watching the
    // auth_expires key that authService writes to localStorage, and also on
    // browser focus / visibility restore / network online events.
    const handleReconnect = () => reconnect();

    const onVisibilityChange = () => {
      if (!document.hidden) reconnect();
    };

    window.addEventListener('storage', (e) => {
      if (e.key === 'auth_expires') handleReconnect();
    });
    window.addEventListener('focus', handleReconnect);
    window.addEventListener('online', handleReconnect);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('storage', handleReconnect);
      window.removeEventListener('focus', handleReconnect);
      window.removeEventListener('online', handleReconnect);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [reconnect]);

  const subscribe = useCallback((fn: RealtimeSubscriber) => {
    subscribersRef.current.add(fn);
    return () => {
      subscribersRef.current.delete(fn);
    };
  }, []);

  return (
    <RealtimeContext.Provider value={{ connected, subscribe }}>
      {children}
    </RealtimeContext.Provider>
  );
}

// ── Drop-in replacement hook (API identical to before) ───────────────────────

export function useRealtimeEvents(handler: RealtimeSubscriber) {
  const ctx = useContext(RealtimeContext);
  const handlerRef = useRef(handler);

  useEffect(() => { handlerRef.current = handler; }, [handler]);

  useEffect(() => {
    if (!ctx) return; // provider not mounted — silent no-op
    const unsubscribe = ctx.subscribe((payload) => handlerRef.current?.(payload));
    return unsubscribe;
  }, [ctx]);

  return { connected: ctx?.connected ?? false };
}
